import { useMutation, useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_DISABILITY_REFERENT, GQL_UPDATE_DISABILITY_REFERENT } from '~/gql/accommodations/accommodations'
import { tourAnchor } from '~/helpers/tour/anchors'

/** Le référent handicap de l'organisme (#327) : son nom et comment le joindre, montrés à l'apprenant. */
function ReferentCard() {
  const { data } = useQuery(GQL_DISABILITY_REFERENT)
  // Le formulaire ne naît qu'avec les valeurs enregistrées : il les prend pour état initial.
  if (!data) return null
  return <ReferentForm initial={data.disabilityReferent} />
}

function ReferentForm({ initial }: { initial: { name?: string | null, contact?: string | null } }) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial.name ?? '')
  const [contact, setContact] = useState(initial.contact ?? '')
  const [save, { loading }] = useMutation(GQL_UPDATE_DISABILITY_REFERENT)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      await save({ variables: { input: { name: name || null, contact: contact || null } } })
      toast.success(t('accommodations.referent.saved'))
    } catch {
      toast.error(t('accommodations.saveFailed'))
    }
  }

  return (
    <Card {...tourAnchor('accommodations.referent')}>
      <CardHeader>
        <CardTitle>{t('accommodations.referent.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={e => void submit(e)} className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">{t('accommodations.referent.help')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="referentName">{t('accommodations.referent.name')}</Label>
              <Input id="referentName" maxLength={150} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="referentContact">{t('accommodations.referent.contact')}</Label>
              <Input id="referentContact" maxLength={300} value={contact} onChange={e => setContact(e.target.value)} />
            </div>
          </div>
          <Button type="submit" className="self-start" disabled={loading}>{t('common.save')}</Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default ReferentCard
